/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `index` for the Caintra Connect frontend.
 * @module modals/B2BEventVirtualLineSchedulesModal/index
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import {
  useEffect,
  useRef,
  useMemo,
  useState,
  useCallback,
  Fragment,
} from "react";
import { useTranslation } from "react-i18next";
import { CircularProgress } from "@mui/material";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import dayjs from "dayjs";

import styles from "./index.module.css";

import { ReactComponent as ViewIcon } from "../../assets/icons/B2BEvents/ViewIcon.svg";

/**
 * @typedef {object} B2BVirtualLineScheduleRow
 * @property {string} key
 * @property {string} buyerUserId
 * @property {string} buyerName
 * @property {string} [fecha]
 * @property {string} [horaInicio]
 * @property {string} [horaFin]
 */

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {string} props.titleTranslationKey - i18n key for modal title
 * @param {B2BVirtualLineScheduleRow[]} props.rows
 * @param {boolean} [props.isLoading]
 * @param {(row: B2BVirtualLineScheduleRow) => void} props.onViewVirtualLine
 * @param {boolean} [props.expandAccordionsOnOpen=false] — When true, all buyer sections start expanded. Default is collapsed for every entry point.
 */
const B2BEventVirtualLineSchedulesModal = ({
  open,
  onClose,
  titleTranslationKey,
  rows,
  isLoading = false,
  onViewVirtualLine,
  expandAccordionsOnOpen = false,
}) => {
  const { t } = useTranslation();
  const dialogRef = useRef(null);
  const hasInitializedExpandedRef = useRef(false);
  const [expandedBuyerIds, setExpandedBuyerIds] = useState(() => new Set());

  const groupedByBuyer = useMemo(() => {
    if (!rows.length) {
      return [];
    }
    const map = new Map();
    for (const row of rows) {
      const id = String(row.buyerUserId ?? "");
      if (id) {
        if (!map.has(id)) {
          map.set(id, {
            buyerUserId: id,
            buyerName: row.buyerName || id,
            rows: [],
          });
        }
        map.get(id).rows.push(row);
      }
    }
    return Array.from(map.values());
  }, [rows]);

  useEffect(() => {
    const el = dialogRef.current;
    if (!el) {
      return;
    }
    if (open) {
      if (!el.open) {
        el.showModal();
      }
    } else {
      el.close();
    }
  }, [open]);

  /**
   * When the dialog opens, set expanded buyer ids once grouped rows exist.
   * `hasInitializedExpandedRef` resets when the dialog closes so reopening reapplies defaults.
   */
  useEffect(() => {
    if (!open) {
      hasInitializedExpandedRef.current = false;
      return;
    }
    if (groupedByBuyer.length === 0) {
      return;
    }
    if (!hasInitializedExpandedRef.current) {
      setExpandedBuyerIds(
        expandAccordionsOnOpen
          ? new Set(groupedByBuyer.map((g) => String(g.buyerUserId)))
          : new Set(),
      );
      hasInitializedExpandedRef.current = true;
    }
  }, [open, groupedByBuyer, expandAccordionsOnOpen]);

  const toggleBuyerExpanded = useCallback((buyerUserId) => {
    const key = String(buyerUserId);
    setExpandedBuyerIds((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  const formatDate = (fecha) => {
    if (!fecha) {
      return "—";
    }
    const d = dayjs(String(fecha).slice(0, 10));
    return d.isValid() ? d.format("L") : String(fecha);
  };

  const formatTime = (v) => {
    if (v == null || String(v).trim() === "") {
      return "—";
    }
    const s = String(v).trim();
    return s.length >= 5 && s[2] === ":" ? s.slice(0, 5) : s;
  };

  if (!open) {
    return null;
  }

  return (
    <>
      <div className={styles.overlay} aria-hidden onClick={onClose} />
      <dialog ref={dialogRef} className={styles.modal}>
        <div className={styles.closeButtonContainer}>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label={t("common.close")}
          >
            ×
          </button>
        </div>
        <h2 className={styles.modalTitle}>{t(titleTranslationKey)}</h2>
        <div className={styles.body}>
          <div className={styles.bodyScroll}>
            {isLoading ? (
              <div className={styles.loaderWrap}>
                <CircularProgress size={40} sx={{ color: "#4eabe7" }} />
              </div>
            ) : rows.length === 0 ? (
              <p className={styles.empty}>
                {t("pages.b2bEventVirtualLineCompanies.modalNoSchedules")}
              </p>
            ) : (
              <div className={styles.accordionList}>
                {groupedByBuyer.map((group) => {
                  const id = String(group.buyerUserId);
                  const expanded = expandedBuyerIds.has(id);
                  return (
                    <div key={id} className={styles.participantUserRowWrapper}>
                      <div className={styles.participantHeaderRow}>
                        <span className={styles.participantUserName}>
                          {group.buyerName || "—"}
                        </span>
                        <button
                          type="button"
                          className={styles.participantHeaderToggle}
                          onClick={() => toggleBuyerExpanded(id)}
                          aria-expanded={expanded}
                        >
                          {expanded ? (
                            <KeyboardArrowUp sx={{ fontSize: 22 }} />
                          ) : (
                            <KeyboardArrowDown sx={{ fontSize: 22 }} />
                          )}
                        </button>
                      </div>
                      {expanded ? (
                        <div className={styles.participantScheduleSection}>
                          <table className={styles.innerTable}>
                            <thead>
                              <tr>
                                <th>
                                  {t(
                                    "pages.b2bEventVirtualLineCompanies.colDate",
                                  )}
                                </th>
                                <th>
                                  {t(
                                    "pages.b2bEventVirtualLineCompanies.colStartTime",
                                  )}
                                </th>
                                <th>
                                  {t(
                                    "pages.b2bEventVirtualLineCompanies.colEndTime",
                                  )}
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {group.rows.map((row) => (
                                <Fragment key={row.key}>
                                  <tr className={styles.scheduleValuesRow}>
                                    <td>{formatDate(row.fecha)}</td>
                                    <td>{formatTime(row.horaInicio)}</td>
                                    <td>{formatTime(row.horaFin)}</td>
                                  </tr>
                                  <tr className={styles.scheduleActionRow}>
                                    <td
                                      colSpan={3}
                                      className={styles.scheduleActionCell}
                                    >
                                      <div
                                        className={styles.scheduleActionInner}
                                      >
                                        <button
                                          type="button"
                                          className={styles.actionButton}
                                          onClick={() => onViewVirtualLine(row)}
                                        >
                                          <ViewIcon
                                            className={styles.actionButtonIcon}
                                            aria-hidden
                                          />
                                          {t(
                                            "pages.b2bEventVirtualLineCompanies.viewVirtualLine",
                                          )}
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                </Fragment>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </dialog>
    </>
  );
};

export default B2BEventVirtualLineSchedulesModal;
